import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LiveMessagingService } from '@headpower/layout';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { ExternalRequest, PermitRequestDialogData } from '../../external-request/external-request.model';
import { TaskTechnicalDetailsService } from 'src/app/tasks/task/details/technicaldetails/task-technicaldetails.service';
import { ExternalRequestService } from '../external-request.service';
import { BlahService } from '@headpower/blah-ng';



@Component({
  selector: 'app-permit-request-dialog',
  templateUrl: './permit-request-dialog.component.html',
  styleUrls: ['./permit-request-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PermitRequestDialogComponent implements OnInit, OnDestroy {

  public mobile: boolean = false;

  public initializing: boolean = true;
  public submitting: boolean = false;
  public pending: boolean = true;

  public permitIdentifier: string = '';
  public permitStatus: string = '';
  public permitDataFetched: string = '';

  public externalRequests: ExternalRequest[] = [];

  public primaryAllowedRequestType: any;
  public secondaryAllowedRequestTypes: any[] = [];

  private targetId: string = '';
  private targetType: string = '';

  private currentUserId: string;

  public failedTooltipMessage: string = '';

  private destroy$: Subject<void> = new Subject();

  constructor(private dialogRef: MatDialogRef<PermitRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private dialogData: PermitRequestDialogData,
    private externalRequestService: ExternalRequestService,
    private liveMessagingService: LiveMessagingService,
    private taskSpecificPropertiesService: TaskTechnicalDetailsService,
    private blahService: BlahService,
    private cdRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.mobile = this.dialogData.mobile;
    this.currentUserId = this.dialogData.currentUserId;
    this.targetId = this.dialogData.targetId;
    this.targetType = this.dialogData.targetType;

    // initial values
    this.permitIdentifier = this.dialogData.permitIdentifier;
    this.permitStatus = this.dialogData.permitStatus;
    this.permitDataFetched = this.dialogData.permitDataFetched;

    // refresh values (in case properties have changed while user has been viewing Task...)
    this.taskSpecificPropertiesService.getProperties(this.targetId);

    this.fetchInitialData();
    this.listenLiveMessages();

    this.taskSpecificPropertiesService.permitRequest$
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.permitIdentifier = res.PermitIdentifier;
        this.permitDataFetched = res.PermitDataFetched;
        this.permitStatus = res.PermitStatus;
        this.cdRef.markForCheck();
      })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getFailedExternalRequestMessage(item: ExternalRequest) {
    this.failedTooltipMessage = `${this.blahService.blah({ key: 'default.externalRequest.requestFailedWithCode', params: [item.RequestStatusCode || '-'] })}\n ${item.RequestResultMessage || '-'}`;
  }

  private fetchInitialData() {
    forkJoin([
      this.externalRequestService.getExternalRequestHistory('Task', this.targetId, 'PermitRequest'),
      this.externalRequestService.getAllowedRequestTypes(this.targetId)
    ]).subscribe({
      next: ([externalRequests, allowedRequestTypes]) => {
        this.externalRequests = externalRequests;
        this.primaryAllowedRequestType = allowedRequestTypes.length > 0 ? allowedRequestTypes.splice(0, 1)[0] : null;
        this.secondaryAllowedRequestTypes = allowedRequestTypes;

        this.initializing = false;

        this.cdRef.markForCheck();
      },
      error: () => {

      }
    });
  }

  private refreshExternalRequestHistory() {
    this.externalRequestService.getExternalRequestHistory('Task', this.targetId, 'PermitRequest')
      .subscribe({
        next: (result) => {
          this.externalRequests = result;
          this.cdRef.markForCheck();
        },
        error: () => {

        }
      });
  }

  private refreshAllowedRequestTypes() {
    this.externalRequestService.getAllowedRequestTypes(this.targetId)
      .subscribe({
        next: (result) => {
          this.primaryAllowedRequestType = result.length > 0 ? result.splice(0, 1)[0] : null;
          this.secondaryAllowedRequestTypes = result;
          this.cdRef.markForCheck();
        },
        error: () => {

        }
      });
  }


  submit(requestType: string) {
    this.externalRequestService.sendPermitRequest({ TaskId: this.targetId, RequestType: requestType })
      .subscribe(res => {
        this.refreshExternalRequestHistory();
        this.refreshAllowedRequestTypes();
      });
  }


  private listenLiveMessages() {
    // triggered when external request is marked as completed/failed
    const externalRequestFinishedTopic = `iwm/externalRequest/permitRequest/${this.targetId}`;
    this.liveMessagingService.listenToSingleMessages(externalRequestFinishedTopic)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.refreshExternalRequestHistory();
        this.refreshAllowedRequestTypes();
      });

    // triggered when new permitrequest event is sent
    const permitRequestSentTopic = `iwm/externalRequest/permitRequestEventSent/${this.targetId}`;
    this.liveMessagingService.listenToSingleMessages(permitRequestSentTopic)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        // currentuser's view is updated after receiving http response, 
        // so no need to refresh it here.
        if (res.CreatorId.toLocaleLowerCase() !== this.currentUserId.toLocaleLowerCase()) {
          this.refreshExternalRequestHistory();
          //this.refreshAllowedRequestTypes(); // TODO: filter available events by already sent events when needed
        }
      });

    // triggered when permit request status changes (NOTE: not same as external request status!)  
    const permitRequestStatusChangedTopic = `iwm/permitRequestStatusChanged/${this.targetId}`;
    this.liveMessagingService.listenToSingleMessages(permitRequestStatusChangedTopic)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.refreshAllowedRequestTypes();
        this.taskSpecificPropertiesService.getProperties(this.targetId);
      });
  }

}
